import { getApiUrl } from '../config/api';

export interface ActivityLog {
  id: number;
  userId: number;
  user: {
    id: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    role: string;
  } | null;
  userType: 'doctor' | 'patient';
  activityType: 'service_price_changed';
  activityJson: {
    service?: string;
    oldPrice?: number;
    newPrice?: number;
    oldEnabled?: boolean;
    newEnabled?: boolean;
    changedBy?: 'doctor' | 'admin';
    changedByUserId?: number;
    doctorId?: number;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ActivityLogFilters {
  page?: number;
  limit?: number;
  userId?: number;
  userType?: 'doctor' | 'patient';
  activityType?: 'service_price_changed';
  startDate?: string;
  endDate?: string;
}

export interface ActivityLogResponse {
  success: boolean;
  message: string;
  data: {
    logs: ActivityLog[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export interface ActivityLogStatistics {
  success: boolean;
  message: string;
  data: {
    total: number;
    byActivityType: {
      [key: string]: number;
    };
    byUserType: {
      [key: string]: number;
    };
  };
}

class ActivityLogService {
  private getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
    };
  }

  async getActivityLogs(filters: ActivityLogFilters = {}): Promise<ActivityLogResponse> {
    const queryParams = new URLSearchParams();
    
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.userId) queryParams.append('userId', filters.userId.toString());
    if (filters.userType) queryParams.append('userType', filters.userType);
    if (filters.activityType) queryParams.append('activityType', filters.activityType);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);

    const response = await fetch(
      `${getApiUrl('/admin/activity-logs')}?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch activity logs');
    }

    return response.json();
  }

  async getActivityLogStatistics(startDate?: string, endDate?: string): Promise<ActivityLogStatistics> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await fetch(
      `${getApiUrl('/admin/activity-logs/statistics')}?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch activity log statistics');
    }

    return response.json();
  }

  async getActivityLogById(id: number): Promise<{ success: boolean; data: ActivityLog }> {
    const response = await fetch(`${getApiUrl(`/admin/activity-logs/${id}`)}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch activity log');
    }

    return response.json();
  }
}

export const activityLogService = new ActivityLogService();

